import React, { useState } from "react";
import { TOKENS } from "../tokens";
import { Card } from "../components/Card";
import { SectionLabel } from "../components/SectionLabel";
import { StatusPill } from "../components/StatusPill";
import { useApp } from "../context/AppContext";
import { CheckCircle2, XCircle, Clock, X, ExternalLink, Trash2 } from "lucide-react";
import api, { getImageUrl, getBackendOrigin } from "../api/client";

const CATEGORY_EMOJI = { Travel: "🚌", Food: "🍽️", Accommodation: "🏨", Other: "📎" };
const FILTERS = ["Pending", "All", "Approved", "Paid", "Rejected"];

export function ReimbursementApprovals() {
  const { team, updateReimbursement, refreshTeam } = useApp();
  const [filter, setFilter] = useState("Pending");
  const [selectedEmp, setSelectedEmp] = useState("ALL");
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [rejectModal, setRejectModal] = useState(null); // { empId, reimId }
  const [rejectReason, setRejectReason] = useState("");
  const [paidModal, setPaidModal] = useState(null); // { empId, reimId }
  const [paymentDate, setPaymentDate] = useState("");
  const [paymentTime, setPaymentTime] = useState("");

  // Flatten all reimbursements with employee info
  const all = team.flatMap((emp) =>
    (emp.reimbursements || []).map((r) => ({
      ...r,
      empId: emp.id,
      employeeId: emp.employeeId,
      empName: emp.name,
      empInitials: emp.initials
    }))
  );

  const monthFiltered = all.filter((r) => {
    if (!r.date) return true;
    return String(r.date).startsWith(selectedMonth);
  });

  const displayed = monthFiltered.filter((r) => {
    if (selectedEmp !== "ALL" && r.employeeId !== selectedEmp && String(r.empId) !== selectedEmp) return false;
    if (filter === "All")      return true;
    if (filter === "Pending")  return r.status === "pending";
    if (filter === "Approved") return r.status === "approved";
    if (filter === "Paid")     return r.status === "paid";
    if (filter === "Rejected") return r.status === "rejected";
    return true;
  });

  const pendingCount   = monthFiltered.filter((r) => r.status === "pending").length;
  const approvedTotal  = monthFiltered.filter((r) => ["approved", "paid"].includes(r.status)).reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const totalClaimed   = monthFiltered.reduce((s, r) => s + (Number(r.amount) || 0), 0);

  // Per-employee monthly breakdown summary
  const empBreakdownMap = {};
  monthFiltered.forEach((r) => {
    const key = r.employeeId || r.empName || "Employee";
    if (!empBreakdownMap[key]) {
      empBreakdownMap[key] = { empName: r.empName, total: 0, approved: 0, pending: 0, rejected: 0, count: 0 };
    }
    const amt = Number(r.amount) || 0;
    empBreakdownMap[key].total += amt;
    empBreakdownMap[key].count += 1;
    if (["approved", "paid"].includes(r.status)) empBreakdownMap[key].approved += amt;
    else if (r.status === "pending") empBreakdownMap[key].pending += amt;
    else if (r.status === "rejected") empBreakdownMap[key].rejected += amt;
  });
  const empBreakdownList = Object.entries(empBreakdownMap).map(([id, data]) => ({ id, ...data }));

  const handleApprove = (empId, reimId) => updateReimbursement(empId, reimId, "approved");

  const handleDelete = async (reimId) => {
    if (!window.confirm("Are you sure you want to permanently delete this reimbursement request?")) return;
    try {
      await api.delete(`/reimbursements/${reimId}`);
      await refreshTeam();
    } catch (err) {
      console.error(err);
      alert("Failed to delete reimbursement");
    }
  };

  const handleReject = () => {
    if (rejectModal) {
      updateReimbursement(rejectModal.empId, rejectModal.reimId, "rejected", rejectReason);
      setRejectModal(null);
      setRejectReason("");
    }
  };

  const handleMarkPaid = () => {
    if (paidModal) {
      updateReimbursement(paidModal.empId, paidModal.reimId, "paid", null, paymentDate, paymentTime);
      setPaidModal(null);
      setPaymentDate("");
      setPaymentTime("");
    }
  };

  const handleDownloadExcel = () => {
    const token = localStorage.getItem("fp_token");
    window.open(`${getBackendOrigin()}/api/export/reimbursements?token=${encodeURIComponent(token || "")}&month=${selectedMonth}`, "_blank");
  };

  const handlePrintAllVouchers = () => {
    const token = localStorage.getItem("fp_token");
    window.open(`${getBackendOrigin()}/api/reimbursements/vouchers/bulk?token=${encodeURIComponent(token || "")}&month=${selectedMonth}`, "_blank");
  };

  return (
    <div>
      {/* Top Action Bar */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <label style={{ fontSize: 12, fontWeight: 700, color: TOKENS.navyDeep }}>Select Month:</label>
          <input
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            style={{
              padding: "6px 10px", borderRadius: 8, border: `1.5px solid ${TOKENS.border}`,
              fontSize: 12, fontWeight: 700, background: "#fff", outline: "none"
            }}
          />
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={handlePrintAllVouchers}
            style={{
              background: TOKENS.primary,
              border: `1.5px solid ${TOKENS.navyDeep}`,
              color: "#fff",
              borderRadius: 10,
              padding: "8px 14px",
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6
            }}
          >
            🖨️ Print All Reimbursement Forms (PDF)
          </button>
          
          <button
            onClick={handleDownloadExcel}
            style={{
              background: TOKENS.successBg,
              border: `1.5px solid ${TOKENS.success}`,
              color: TOKENS.success,
              borderRadius: 10,
              padding: "8px 14px",
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6
            }}
          >
            📊 Export Monthly Reimbursements to Excel
          </button>
        </div>
      </div>

      {/* Summary KPI Tiles */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
        <Card style={{ flex: 1, padding: 14, textAlign: "center" }}>
          <div style={{ fontFamily: "Fraunces, serif", fontSize: 22, fontWeight: 700, color: TOKENS.warning }}>
            {pendingCount}
          </div>
          <div style={{ fontSize: 10, color: TOKENS.muted, marginTop: 2, letterSpacing: 0.5 }}>AWAITING APPROVAL</div>
        </Card>
        <Card style={{ flex: 1, padding: 14, textAlign: "center" }}>
          <div style={{ fontFamily: "Fraunces, serif", fontSize: 22, fontWeight: 700, color: TOKENS.success }}>
            ₹{approvedTotal.toLocaleString("en-IN")}
          </div>
          <div style={{ fontSize: 10, color: TOKENS.muted, marginTop: 2, letterSpacing: 0.5 }}>APPROVED THIS MONTH</div>
        </Card>
        <Card style={{ flex: 1, padding: 14, textAlign: "center" }}>
          <div style={{ fontFamily: "Fraunces, serif", fontSize: 22, fontWeight: 700, color: TOKENS.navyDeep }}>
            ₹{totalClaimed.toLocaleString("en-IN")}
          </div>
          <div style={{ fontSize: 10, color: TOKENS.muted, marginTop: 2, letterSpacing: 0.5 }}>TOTAL CLAIMED</div>
        </Card>
      </div>

      {/* Per-Employee Monthly Expense Breakdown */}
      {empBreakdownList.length > 0 && (
        <div style={{ marginBottom: 18 }}>
          <SectionLabel>👤 Per-Employee Monthly Reimbursement Summary ({selectedMonth})</SectionLabel>
          <Card style={{ padding: 0, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5, textAlign: "left" }}>
              <thead>
                <tr style={{ background: TOKENS.cream, borderBottom: `1px solid ${TOKENS.border}` }}>
                  <th style={{ padding: "10px 12px", color: TOKENS.navyDeep }}>Employee</th>
                  <th style={{ padding: "10px 12px", color: TOKENS.navyDeep }}>Total Claims</th>
                  <th style={{ padding: "10px 12px", color: TOKENS.success }}>Approved</th>
                  <th style={{ padding: "10px 12px", color: TOKENS.warning }}>Pending</th>
                  <th style={{ padding: "10px 12px", color: TOKENS.danger }}>Rejected</th>
                </tr>
              </thead>
              <tbody>
                {empBreakdownList.map((emp) => (
                  <tr key={emp.id} style={{ borderBottom: `1px solid ${TOKENS.border}` }}>
                    <td style={{ padding: "10px 12px", fontWeight: 700, color: TOKENS.ink }}>
                      {emp.empName || emp.id}
                      <div style={{ fontSize: 10.5, color: TOKENS.muted, fontWeight: 400 }}>{emp.id}</div>
                    </td>
                    <td style={{ padding: "10px 12px", fontWeight: 700 }}>₹{emp.total.toLocaleString("en-IN")} ({emp.count})</td>
                    <td style={{ padding: "10px 12px", color: TOKENS.success, fontWeight: 700 }}>₹{emp.approved.toLocaleString("en-IN")}</td>
                    <td style={{ padding: "10px 12px", color: TOKENS.warning, fontWeight: 700 }}>₹{emp.pending.toLocaleString("en-IN")}</td>
                    <td style={{ padding: "10px 12px", color: TOKENS.danger, fontWeight: 700 }}>₹{emp.rejected.toLocaleString("en-IN")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      )}

      {/* Filter Row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "14px 0", flexWrap: "wrap", gap: 10 }}>
        <div style={{ display: "flex", gap: 8, overflowX: "auto" }}>
          {FILTERS.map((f) => (
            <button key={f} onClick={() => setFilter(f)} style={{
              padding: "6px 14px", borderRadius: 20,
              background: filter === f ? TOKENS.navyDeep : "#fff",
              color: filter === f ? "#fff" : TOKENS.muted,
              border: `1.5px solid ${filter === f ? TOKENS.navyDeep : TOKENS.border}`,
              fontSize: 12, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap",
            }}>{f}</button>
          ))}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <label style={{ fontSize: 11.5, fontWeight: 700, color: TOKENS.navyDeep }}>Employee Filter:</label>
          <select
            value={selectedEmp}
            onChange={(e) => setSelectedEmp(e.target.value)}
            style={{
              padding: "6px 10px", borderRadius: 8, border: `1.5px solid ${TOKENS.border}`,
              fontSize: 12, fontWeight: 700, background: "#fff", outline: "none"
            }}
          >
            <option value="ALL">All Employees</option>
            {team.map((e) => (
              <option key={e.employeeId || e.id} value={e.employeeId || e.id}>{e.name} ({e.employeeId})</option>
            ))}
          </select>
        </div>
      </div>

      {/* Requests */}
      {displayed.length === 0 && (
        <Card style={{ textAlign: "center", color: TOKENS.muted, fontSize: 13, padding: 24 }}>
          No {filter.toLowerCase()} requests.
        </Card>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {displayed.map((r) => (
          <Card key={r.id} style={{ padding: 14 }}>
            {/* Employee tag */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <div style={{
                width: 28, height: 28, borderRadius: "50%", background: TOKENS.navyMid,
                color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 10, fontWeight: 700, flexShrink: 0,
              }}>{r.empInitials}</div>
              <span style={{ fontSize: 13, fontWeight: 700, color: TOKENS.navyDeep }}>{r.empName}</span>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 14, display: "flex", alignItems: "center", gap: 8 }}>
                  {CATEGORY_EMOJI[r.category] || "📎"} {r.category}
                  <button
                    onClick={() => handleDelete(r.id)}
                    style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '0 4px', display: 'flex', alignItems: 'center' }}
                    title="Delete Reimbursement"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                <div style={{ fontSize: 12, color: TOKENS.muted, marginTop: 2 }}>{r.description}</div>
                <div style={{ fontSize: 11, color: TOKENS.muted, marginTop: 2 }}>{r.date}</div>
                {r.rejectReason && (
                  <div style={{ fontSize: 11, color: TOKENS.danger, marginTop: 4, fontWeight: 600 }}>
                    Reason: {r.rejectReason}
                  </div>
                )}
                {r.status === "paid" && (r.paymentDate || r.paymentTime) && (
                  <div style={{ fontSize: 11, color: TOKENS.success, marginTop: 4, fontWeight: 600 }}>
                    Paid on: {r.paymentDate} {r.paymentTime}
                  </div>
                )}
                {(r.receiptUrl || r.receipt) && (
                  <div style={{ marginTop: 10, background: TOKENS.cream, borderRadius: 10, padding: 8, border: `1px solid ${TOKENS.border}`, display: "inline-flex", alignItems: "center", gap: 10 }}>
                    {(r.receiptUrl || r.receipt)?.match(/\.(jpg|jpeg|png|gif|webp)$/i) || (r.receiptUrl || r.receipt)?.startsWith("data:image") ? (
                      <img
                        src={getImageUrl(r.receiptUrl || r.receipt)}
                        alt="Receipt"
                        style={{ width: 44, height: 44, borderRadius: 8, objectFit: "cover", border: `1px solid ${TOKENS.border}` }}
                      />
                    ) : (
                      <div style={{ width: 44, height: 44, borderRadius: 8, background: `${TOKENS.navyDeep}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>🧾</div>
                    )}
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: TOKENS.navyDeep }}>Bill / Receipt Attached</div>
                      <a
                        href={getImageUrl(r.receiptUrl || r.receipt)}
                        target="_blank"
                        rel="noopener noreferrer"
                        download
                        style={{
                          display: "inline-flex", alignItems: "center", gap: 4, marginTop: 2,
                          fontSize: 11, fontWeight: 700, color: TOKENS.navyDeep, textDecoration: "underline"
                        }}
                      >
                        <ExternalLink size={12} /> Open & Download Receipt
                      </a>
                    </div>
                  </div>
                )}
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontFamily: "Fraunces, serif", fontSize: 18, fontWeight: 700, color: TOKENS.navyDeep }}>
                  ₹{r.amount.toLocaleString("en-IN")}
                </div>
                <StatusPill status={r.status} style={{ marginTop: 4, display: "inline-block" }} />
                <button
                  onClick={() => {
                    const token = sessionStorage.getItem("fp_token");
                    window.open(`${getBackendOrigin()}/api/reimbursements/${r.id}/voucher?token=${encodeURIComponent(token || "")}`, "_blank");
                  }}
                  style={{
                    display: "flex", alignItems: "center", gap: 6, marginTop: 10, marginLeft: "auto",
                    background: "#fff", color: TOKENS.navyDeep, border: `1.5px solid ${TOKENS.border}`,
                    borderRadius: 8, padding: "6px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer",
                  }}
                >
                  📄 Download Reimbursement Form
                </button>
              </div>
            </div>

            {r.status === "pending" && (
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <button
                  onClick={() => handleApprove(r.empId, r.id)}
                  style={{
                    flex: 1, background: TOKENS.success, color: "#fff", border: "none",
                    borderRadius: 10, padding: "9px 0", fontWeight: 700, fontSize: 12.5,
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                    cursor: "pointer",
                  }}
                >
                  <CheckCircle2 size={15} /> Approve
                </button>
                <button
                  onClick={() => { setRejectModal({ empId: r.empId, reimId: r.id }); setRejectReason(""); }}
                  style={{
                    flex: 1, background: "#fff", color: TOKENS.danger,
                    border: `1.5px solid ${TOKENS.danger}`, borderRadius: 10, padding: "9px 0",
                    fontWeight: 700, fontSize: 12.5,
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                    cursor: "pointer",
                  }}
                >
                  <XCircle size={15} /> Reject
                </button>
              </div>
            )}

            {r.status === "approved" && (
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <button
                  onClick={() => {
                    setPaidModal({ empId: r.empId, reimId: r.id });
                    const now = new Date();
                    setPaymentDate(now.toLocaleDateString('en-CA'));
                    setPaymentTime(now.toTimeString().slice(0, 5));
                  }}
                  style={{
                    flex: 1, background: TOKENS.success, color: "#fff", border: "none",
                    borderRadius: 10, padding: "9px 0", fontWeight: 700, fontSize: 12.5,
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                    cursor: "pointer",
                  }}
                >
                  💳 Mark Paid
                </button>
              </div>
            )}
          </Card>
        ))}
      </div>

      {/* Reject reason modal overlay */}
      {rejectModal && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(10,21,45,0.5)",
          backdropFilter: "blur(6px)",
          display: "flex", alignItems: "flex-start", paddingTop: 80, justifyContent: "center",
          zIndex: 9999, padding: 16,
        }}>
          <div style={{
            background: "#fff", borderRadius: 20, padding: 24,
            width: "100%", maxWidth: 420, boxShadow: "0 24px 60px rgba(10,25,50,0.22)",
            border: `1.5px solid ${TOKENS.border}`,
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
              <div style={{ fontFamily: "Fraunces, serif", fontSize: 17, fontWeight: 700, color: TOKENS.navyDeep }}>
                Reject Reimbursement Request
              </div>
              <button onClick={() => setRejectModal(null)} style={{ border: "none", background: "none", cursor: "pointer" }}>
                <X size={18} color={TOKENS.muted} />
              </button>
            </div>
            <textarea
              placeholder="Explain why this request is being rejected…"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              style={{
                width: "100%", padding: "10px 12px", borderRadius: 10,
                border: `1.5px solid ${TOKENS.border}`, fontSize: 13, outline: "none",
                background: TOKENS.cream, resize: "none", marginBottom: 14,
              }}
            />
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setRejectModal(null)} style={{
                flex: 1, padding: "10px 14px", borderRadius: 10,
                border: `1.5px solid ${TOKENS.border}`, background: "#fff",
                color: TOKENS.ink, fontWeight: 700, fontSize: 13, cursor: "pointer",
              }}>
                Cancel
              </button>
              <button onClick={handleReject} style={{
                flex: 1, background: TOKENS.danger, color: "#fff",
                border: "none", borderRadius: 10, padding: "10px 14px",
                fontWeight: 700, fontSize: 13, cursor: "pointer",
              }}>
                Confirm Rejection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Paid Modal Overlay */}
      {paidModal && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(10,21,45,0.5)",
          backdropFilter: "blur(6px)",
          display: "flex", alignItems: "flex-start", paddingTop: 80, justifyContent: "center",
          zIndex: 9999, padding: 16,
        }}>
          <div style={{
            background: "#fff", borderRadius: 20, padding: 24,
            width: "100%", maxWidth: 420, boxShadow: "0 24px 60px rgba(10,25,50,0.22)",
            border: `1.5px solid ${TOKENS.border}`,
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
              <div style={{ fontFamily: "Fraunces, serif", fontSize: 17, fontWeight: 700, color: TOKENS.navyDeep }}>
                Record Payment Details
              </div>
              <button onClick={() => setPaidModal(null)} style={{ border: "none", background: "none", cursor: "pointer" }}>
                <X size={18} color={TOKENS.muted} />
              </button>
            </div>
            
            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 16 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: TOKENS.navyDeep }}>Payment Date</label>
                <input
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  style={{
                    padding: "10px 12px", borderRadius: 10,
                    border: `1.5px solid ${TOKENS.border}`, fontSize: 13, outline: "none",
                    background: TOKENS.cream,
                  }}
                />
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: TOKENS.navyDeep }}>Payment Time</label>
                <input
                  type="time"
                  value={paymentTime}
                  onChange={(e) => setPaymentTime(e.target.value)}
                  style={{
                    padding: "10px 12px", borderRadius: 10,
                    border: `1.5px solid ${TOKENS.border}`, fontSize: 13, outline: "none",
                    background: TOKENS.cream,
                  }}
                />
              </div>
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setPaidModal(null)} style={{
                flex: 1, padding: "10px 14px", borderRadius: 10,
                border: `1.5px solid ${TOKENS.border}`, background: "#fff",
                color: TOKENS.ink, fontWeight: 700, fontSize: 13, cursor: "pointer",
              }}>
                Cancel
              </button>
              <button onClick={handleMarkPaid} style={{
                flex: 1, background: TOKENS.success, color: "#fff",
                border: "none", borderRadius: 10, padding: "10px 14px",
                fontWeight: 700, fontSize: 13, cursor: "pointer",
              }}>
                Confirm Paid
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
